import pico from "./pico";

let cascadeClassifier = null;
let cascadeLoadingPromise = null;

/**
 * Convert RGBA pixel array to 8-bit grayscale for Pico.js
 */
function rgbaToGrayscale(rgba, nrows, ncols) {
  const gray = new Uint8Array(nrows * ncols);
  for (let r = 0; r < nrows; ++r) {
    for (let c = 0; c < ncols; ++c) {
      const idx = 4 * (r * ncols + c);
      // Fast integer luminance: (2*R + 7*G + 1*B) / 10
      gray[r * ncols + c] = (2 * rgba[idx] + 7 * rgba[idx + 1] + 1 * rgba[idx + 2]) / 10;
    }
  }
  return gray;
}

/**
 * Lazy loads the local Facefinder cascade model (offline, stored in /models/facefinder)
 */
export async function initFaceDetector() {
  if (cascadeClassifier) return cascadeClassifier;
  if (cascadeLoadingPromise) return cascadeLoadingPromise;

  cascadeLoadingPromise = (async () => {
    try {
      const res = await fetch("/models/facefinder");
      if (!res.ok) {
        throw new Error(`Failed to load facefinder model: ${res.status}`);
      }
      const buffer = await res.arrayBuffer();
      const bytes = new Int8Array(buffer);
      cascadeClassifier = pico.unpack_cascade(bytes);
      return cascadeClassifier;
    } catch (err) {
      console.warn("Could not load local facefinder cascade, will use native/fallback:", err);
      return null;
    }
  })();

  return cascadeLoadingPromise;
}

/**
 * Fallback skin tone & variance check in case cascade or native is unavailable
 */
function heuristicFacialCheck(ctx, width, height) {
  try {
    const centerW = Math.floor(width * 0.6);
    const centerH = Math.floor(height * 0.6);
    const startX = Math.floor((width - centerW) / 2);
    const startY = Math.floor((height - centerH) / 2);

    const imgData = ctx.getImageData(startX, startY, centerW, centerH);
    const data = imgData.data;

    let skinPixels = 0;
    let totalSamples = 0;
    let brightnessSum = 0;

    for (let i = 0; i < data.length; i += 16) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      totalSamples++;
      brightnessSum += (r + g + b) / 3;

      // Common human skin tone bounds in RGB space
      if (r > 45 && g > 30 && b > 20 && r > g && r > b && Math.abs(r - g) >= 10) {
        skinPixels++;
      }
    }

    const skinRatio = skinPixels / Math.max(1, totalSamples);
    const avgBrightness = brightnessSum / Math.max(1, totalSamples);

    // If substantial skin cluster is present and frame is not pitch black or washed out
    const hasFaceLikeRegion = skinRatio > 0.18 && avgBrightness > 35 && avgBrightness < 240;
    return {
      hasFace: hasFaceLikeRegion,
      count: hasFaceLikeRegion ? 1 : 0,
      confidence: skinRatio,
      method: "heuristic",
    };
  } catch (e) {
    return { hasFace: true, count: 1, confidence: 0.5, method: "bypass" };
  }
}

/**
 * Detect faces in an HTMLVideoElement, HTMLCanvasElement, HTMLImageElement, or Base64/DataURL
 *
 * @param {HTMLVideoElement | HTMLCanvasElement | HTMLImageElement | string} source
 * @returns {Promise<{ hasFace: boolean, count: number, confidence: number, method: string }>}
 */
export async function detectFace(source) {
  if (!source) {
    return { hasFace: false, count: 0, confidence: 0, method: "none" };
  }

  // 1. Prepare offscreen canvas (scaled to 320x240 for 60fps real-time speed)
  const targetWidth = 320;
  const targetHeight = 240;
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  if (typeof source === "string") {
    // DataURL or image URL
    await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        resolve();
      };
      img.onerror = reject;
      img.src = source;
    });
  } else if (source instanceof HTMLVideoElement) {
    if (source.videoWidth === 0 || source.videoHeight === 0) {
      return { hasFace: false, count: 0, confidence: 0, method: "waiting_for_video" };
    }
    ctx.drawImage(source, 0, 0, targetWidth, targetHeight);
  } else {
    // Canvas or Image element
    ctx.drawImage(source, 0, 0, targetWidth, targetHeight);
  }

  // 2. Strategy A: Native Browser Shape Detection API (if supported, e.g. Chrome / Android Chrome)
  if (typeof window !== "undefined" && "FaceDetector" in window) {
    try {
      const faceDetector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 3 });
      const detectedFaces = await faceDetector.detect(canvas);
      if (detectedFaces && detectedFaces.length > 0) {
        return {
          hasFace: true,
          count: detectedFaces.length,
          confidence: 0.95,
          method: "native_api",
        };
      }
    } catch (e) {
      // Fallback to Pico.js cascade
    }
  }

  // 3. Strategy B: Pico.js Cascade (Ultra-lightweight ~2KB pure JS classifier)
  try {
    const classifier = await initFaceDetector();
    if (classifier) {
      const rgba = ctx.getImageData(0, 0, targetWidth, targetHeight).data;
      const image = {
        pixels: rgbaToGrayscale(rgba, targetHeight, targetWidth),
        nrows: targetHeight,
        ncols: targetWidth,
        ldim: targetWidth,
      };

      const params = {
        shiftfactor: 0.1, // 10% slide
        minsize: 35, // Min face size (pixels)
        maxsize: 220, // Max face size
        scalefactor: 1.15,
      };

      let detections = pico.run_cascade(image, classifier, params);
      detections = pico.cluster_detections(detections, 0.2);

      // Score threshold > 10.0 provides high precision without false alarms
      const validFaces = detections.filter((d) => d[3] > 8.0);

      if (validFaces.length > 0) {
        return {
          hasFace: true,
          count: validFaces.length,
          confidence: validFaces[0][3],
          method: "pico_cascade",
        };
      }
    }
  } catch (err) {
    console.warn("Pico face detection error:", err);
  }

  // 4. Strategy C: Heuristic fallback
  return heuristicFacialCheck(ctx, targetWidth, targetHeight);
}
